import { DocumentReference } from '../components/ChatInDoc/DocumentReferences';

interface RawDocumentReference {
  id?: string;
  documentId?: string;
  documentContentId?: string;
  title?: string;
  documentTitle?: string;
  path?: string;
  documentPath?: string;
  filePath?: string;
  description?: string;
  documentDescription?: string;
  chunk?: string;
  content?: string;
  text?: string;
  score?: number;
  similarity?: number;
  relevanceScore?: number;
  chunkIndex?: number;
  metadata?: DocumentReference['metadata'];
}

const FONTE_LINE_PATTERN = /^Fonte:\s*(.+?)\s*$/im;

const fonteLineRegex = (): RegExp => new RegExp(FONTE_LINE_PATTERN.source, 'gim');

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value))
  {
    return value as Record<string, unknown>;
  }

  return null;
};

const asString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0)
  {
    return value;
  }

  return undefined;
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value))
  {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0)
  {
    const parsed = Number(value);
    if (Number.isFinite(parsed))
    {
      return parsed;
    }
  }

  return undefined;
};

const mapRawReference = (source: RawDocumentReference, index: number): DocumentReference => {
  const title = source.title || source.documentTitle || 'Documento';
  const path = source.path || source.documentPath || source.filePath;
  const score = source.relevanceScore ?? source.score ?? source.similarity;

  return {
    id: source.id || source.documentId || source.documentContentId || `source-${index}`,
    documentId: source.documentId,
    title,
    documentTitle: source.documentTitle || title,
    path,
    filePath: source.filePath || path,
    description: source.description,
    documentDescription: source.documentDescription,
    chunk: source.text || source.content || source.chunk,
    content: source.content || source.text,
    text: source.text || source.content || source.chunk,
    score,
    relevanceScore: source.relevanceScore ?? score,
    chunkIndex: source.chunkIndex,
    metadata: source.metadata,
  };
};

const parseAttributesReferences = (contentAttributes?: string | null): DocumentReference[] => {
  if (!contentAttributes)
  {
    return [];
  }

  try
  {
    const parsed = JSON.parse(contentAttributes) as unknown;
    const root = asRecord(parsed);
    if (!root)
    {
      return [];
    }

    const raw = root.documentReferences;
    if (!Array.isArray(raw))
    {
      return [];
    }

    return raw
      .map((item, index) =>
      {
        const record = asRecord(item);
        if (!record)
        {
          return null;
        }

        return mapRawReference({
          id: asString(record.id),
          documentId: asString(record.documentId),
          documentContentId: asString(record.documentContentId),
          title: asString(record.title),
          documentTitle: asString(record.documentTitle),
          path: asString(record.path),
          documentPath: asString(record.documentPath),
          filePath: asString(record.filePath),
          description: asString(record.description),
          documentDescription: asString(record.documentDescription),
          chunk: asString(record.chunk),
          content: asString(record.content),
          text: asString(record.text),
          score: asNumber(record.score),
          similarity: asNumber(record.similarity),
          relevanceScore: asNumber(record.relevanceScore),
          chunkIndex: asNumber(record.chunkIndex),
          metadata: asRecord(record.metadata) as DocumentReference['metadata'],
        }, index);
      })
      .filter((item): item is DocumentReference => item !== null);
  }
  catch
  {
    return [];
  }
};

const parseFonteLine = (line: string): DocumentReference | null => {
  const withoutPrefix = line.replace(/^Fonte:\s*/i, '').replace(/\.\s*$/, '').trim();
  if (!withoutPrefix)
  {
    return null;
  }

  const parenMatch = withoutPrefix.match(/^(.*?)\s*\((.*)\)\s*$/);
  const title = (parenMatch?.[1] ?? withoutPrefix).trim();
  const sections = parenMatch?.[2]?.trim();

  if (!title)
  {
    return null;
  }

  return {
    id: `fonte-${title}`,
    title,
    documentTitle: title,
    path: title.startsWith('/') ? title : `/${title}`,
    description: sections ? `Seção: ${sections}` : undefined,
  };
};

export const parseFonteFallbackReferences = (content?: string | null): DocumentReference[] => {
  if (!content)
  {
    return [];
  }

  const matches = content.match(fonteLineRegex()) ?? [];
  const references: DocumentReference[] = [];

  matches.forEach((line) =>
  {
    const parsed = parseFonteLine(line);
    if (parsed)
    {
      references.push(parsed);
    }
  });

  return references;
};

export const parseSupportDocumentReferences = (
  contentAttributes?: string | null,
  content?: string | null,
): DocumentReference[] => {
  const fromAttributes = parseAttributesReferences(contentAttributes);
  if (fromAttributes.length > 0)
  {
    return fromAttributes;
  }

  return parseFonteFallbackReferences(content);
};

export const stripFonteLines = (content?: string | null): string => {
  if (!content)
  {
    return '';
  }

  return content
    .replace(fonteLineRegex(), '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
};
